/** @jsxImportSource @emotion/react */
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { NotesIndicator } from 'components/lab/billingNotes/BillingNotes'
import { formatCalendarDate, formatEasternDateTime } from 'helpers/dates'
import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { useGetChargeNeedsWorkQuery } from 'store/services/needsWork'
import { NeedsWorkItemDto } from 'store/types'
import { FOLLOW_UP_COLORS, flaggedFor, followUpState, REMIT_RESOLUTION_LABELS } from './needsWork'
import { ParkedRemitTable } from './ParkedRemitTable'
import { useNeedsWorkActions } from './useNeedsWorkActions'

const flaggedPhrase = (createdAt: string): string => {
  const age = flaggedFor(createdAt)

  return age === 'today' ? 'today' : `${age} ago`
}

type ItemProps = {
  item: NeedsWorkItemDto
  canEdit: boolean
  canResolve: boolean
  onEdit: () => void
  onResolve: () => void
  onNotes: () => void
}

const OpenItem: React.FC<ItemProps> = ({ item, canEdit, canResolve, onEdit, onResolve, onNotes }) => {
  const [remitOpen, setRemitOpen] = useState(true)
  const holdsRemit = Boolean(item.remit?.parked)
  const due = followUpState(item.followUpDate)

  return (
    <div
      data-testid="needs-work-item"
      css={{
        background: '#fff4e5',
        border: '1px solid #ffd8a8',
        color: '#6b3a00',
        borderRadius: 5,
        padding: '10px 20px',
        marginBottom: 12,
      }}>
      <div className="d-flex align-items-center" css={{ gap: 10 }}>
        <i className="fas fa-flag text-danger" />
        <div css={{ fontWeight: 600 }}>Needs Work — {item.reasonLabel}</div>
        <div className="ml-auto d-flex align-items-center" css={{ gap: 8 }}>
          <NotesIndicator count={item.notesCount} isOpen={false} onToggle={onNotes} />
          {canEdit && (
            <Button size="sm" variant="outline-secondary" onClick={onEdit}>
              Edit
            </Button>
          )}
          {canResolve && (
            <Button size="sm" variant="success" onClick={onResolve}>
              Resolve
            </Button>
          )}
        </div>
      </div>
      <div css={{ fontSize: '0.8rem', marginTop: 4 }}>
        Owner <b>{item.owner?.name || '—'}</b>
        {' · '}Follow-up{' '}
        <b css={{ color: due ? FOLLOW_UP_COLORS[due] : undefined }}>
          {formatCalendarDate(item.followUpDate, 'none')}
          {due === 'OVERDUE' ? ' (overdue)' : due === 'TODAY' ? ' (today)' : ''}
        </b>
        {' · '}Flagged {flaggedPhrase(item.createdAt)} by {item.createdBy?.name || 'the system'}
      </div>
      {item.remit && (
        <div css={{ marginTop: 6 }}>
          <span
            css={{ cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, userSelect: 'none' }}
            onClick={() => setRemitOpen((open) => !open)}>
            {remitOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            {holdsRemit ? 'Parked remit — how the payer applied it (nothing posted)' : 'Remit'}
          </span>
          {remitOpen && <ParkedRemitTable remit={item.remit} />}
        </div>
      )}
    </div>
  )
}

const History: React.FC<{ items: NeedsWorkItemDto[]; onNotes: (item: NeedsWorkItemDto) => void }> = ({ items, onNotes }) => {
  const [open, setOpen] = useState(false)

  return (
    <div css={{ marginBottom: 12 }}>
      <span
        css={{ cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, userSelect: 'none', color: '#4a5060' }}
        onClick={() => setOpen((value) => !value)}>
        {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        Needs Work history ({items.length})
      </span>
      {open && (
        <div className="table-responsive">
          <table className="table table-sm mb-0" css={{ fontSize: '0.8rem', marginTop: 6 }}>
            <thead>
              <tr>
                <th>Raised</th>
                <th>Reason</th>
                <th>Owner</th>
                <th>Resolved</th>
                <th>Outcome</th>
                <th>Closing note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {formatEasternDateTime(item.createdAt, '—')}
                    <div className="text-muted">by {item.createdBy?.name || 'the system'}</div>
                  </td>
                  <td>{item.reasonLabel}</td>
                  <td>{item.owner?.name || '—'}</td>
                  <td>
                    {formatEasternDateTime(item.resolvedAt, '—')}
                    <div className="text-muted">by {item.resolvedBy?.name || '—'}</div>
                  </td>
                  <td>{item.remitResolution ? REMIT_RESOLUTION_LABELS[item.remitResolution] : 'Resolved'}</td>
                  <td css={{ whiteSpace: 'pre-wrap' }}>{item.resolutionNote || '—'}</td>
                  <td>
                    <NotesIndicator count={item.notesCount} isOpen={false} onToggle={() => onNotes(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * The charge's Needs Work: every open item as a banner — its reason, owner,
 * follow-up date and, when it was raised from a remit, how the payer applied
 * that remit — and the resolved ones as the charge's history of them.
 */
export const NeedsWorkChargeSection: React.FC<{ chargeId: string }> = ({ chargeId }) => {
  const { data: items = [] } = useGetChargeNeedsWorkQuery(chargeId)
  const actions = useNeedsWorkActions()

  if (!items.length) {
    return null
  }

  const open = items.filter((item) => item.status === 'OPEN')
  const resolved = items.filter((item) => item.status !== 'OPEN')

  return (
    <div data-testid="needs-work-section">
      {open.map((item) => (
        <OpenItem
          key={item.id}
          item={item}
          canEdit={actions.canModify}
          canResolve={actions.canResolve(item)}
          onEdit={() => actions.edit(item)}
          onResolve={() => actions.resolve(item)}
          onNotes={() => actions.notes(item)}
        />
      ))}
      {resolved.length > 0 && <History items={resolved} onNotes={actions.notes} />}
      {actions.modals}
    </div>
  )
}
